/* Client-side image compressor used before base64-encoding a logo/photo.
 * Draws the source image to a canvas resized to MAX_SIDE preserving aspect,
 * then returns a data URI. Falls back to the original file for SVG or already
 * small images.
 */

const MAX_SIDE = 512;         // px
const QUALITY = 0.85;         // JPEG quality
const SKIP_UNDER_BYTES = 200 * 1024; // 200KB — no gain compressing under this

/** Read a File as a data URL string. */
const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(file);
  });

/** Load an <img> from a data URL. */
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('decode failed'));
    img.src = src;
  });

/**
 * Compress an image file client-side.
 *
 * @param {File} file
 * @param {{maxSide?: number, quality?: number, mime?: string}} [opts]
 * @returns {Promise<{dataUrl: string, width: number, height: number,
 *                    originalSize: number, compressedSize: number, skipped: boolean}>}
 */
export async function compressImage(file, opts = {}) {
  const maxSide = opts.maxSide || MAX_SIDE;
  const quality = opts.quality ?? QUALITY;

  // SVG: keep as-is (vector, tiny)
  if (file.type === 'image/svg+xml') {
    const dataUrl = await readAsDataURL(file);
    return { dataUrl, width: 0, height: 0,
             originalSize: file.size, compressedSize: file.size, skipped: true };
  }

  // Small enough already: keep as-is
  if (file.size <= SKIP_UNDER_BYTES) {
    const dataUrl = await readAsDataURL(file);
    return { dataUrl, width: 0, height: 0,
             originalSize: file.size, compressedSize: file.size, skipped: true };
  }

  const srcDataUrl = await readAsDataURL(file);
  const img = await loadImage(srcDataUrl);

  let { width, height } = img;
  if (width > maxSide || height > maxSide) {
    const ratio = width / height;
    if (ratio >= 1) { width = maxSide; height = Math.round(maxSide / ratio); }
    else { height = maxSide; width = Math.round(maxSide * ratio); }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Preserve transparency for PNG; otherwise JPEG (smaller).
  const preservesAlpha = file.type === 'image/png' || file.type === 'image/webp';
  const outMime = opts.mime || (preservesAlpha ? 'image/png' : 'image/jpeg');
  const dataUrl = canvas.toDataURL(outMime, quality);

  // Estimate compressed size from data URI payload length
  const payload = dataUrl.split(',')[1] || '';
  const compressedSize = Math.round(payload.length * 3 / 4);

  return { dataUrl, width, height,
           originalSize: file.size, compressedSize, skipped: false };
}

/** Human-readable byte formatter (KB / MB). */
export function fmtBytes(n) {
  if (!n) return '0 o';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}
